import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchRoutines, fetchSkinTypes } from "../actions/routineActions";
import { useImage } from "../contexts/ImageContext";
import formatMoney from "../components/formatMoney";
import "../styles/routines.scss";
import { RoutinesGridSkeleton } from "../components/HomeSkeletons";

const Routines = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getImageUrl } = useImage();

  const { routines, skinTypes, loading, skinTypesLoading, totalCount } =
    useSelector((state) => state.routines);

  const [selectedSkinType, setSelectedSkinType] = useState(
    searchParams.get("skin_type") || ""
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");

  useEffect(() => {
    document.title = "Skincare Routines | K-Shop";
    dispatch(fetchSkinTypes());
  }, [dispatch]);

  useEffect(() => {
    const params = {
      page: 1,
      per_page: 20,
    };
    if (selectedSkinType) params.skin_type_id = selectedSkinType;
    if (searchQuery) params.search = searchQuery;

    dispatch(fetchRoutines(params));
  }, [dispatch, selectedSkinType, searchQuery]);

  const handleSkinTypeChange = (skinTypeId) => {
    setSelectedSkinType(skinTypeId);
    if (skinTypeId) {
      searchParams.set("skin_type", skinTypeId);
    } else {
      searchParams.delete("skin_type");
    }
    setSearchParams(searchParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery) {
      searchParams.set("q", searchQuery);
    } else {
      searchParams.delete("q");
    }
    setSearchParams(searchParams);
  };

  const handleRoutineClick = (routine) => {
    navigate(`/routines/${routine.slug || routine.routine_id}`);
  };

  return (
    <div className="routines-page">
      {/* Hero Section */}
      <div className="routines-hero">
        <div className="hero-content">
          <h1>Skincare Routines</h1>
          <p>
            Discover curated skincare routines designed for your unique skin
            type. Get expert-recommended products bundled together for the best
            results.
          </p>
        </div>
      </div>

      <div className="routines-container">
        {/* Filters Section */}
        <div className="routines-filters">
          <div className="filter-section">
            <h3>Filter by Skin Type</h3>
            <div className="skin-type-pills">
              <button
                className={`skin-pill ${selectedSkinType === "" ? "active" : ""}`}
                onClick={() => handleSkinTypeChange("")}
              >
                All Types
              </button>
              {skinTypes.map((st) => (
                <button
                  key={st.skin_type_id}
                  className={`skin-pill ${
                    selectedSkinType === st.skin_type_id ? "active" : ""
                  }`}
                  onClick={() => handleSkinTypeChange(st.skin_type_id)}
                >
                  {st.icon && <span className="pill-icon">{st.icon}</span>}
                  {st.name}
                </button>
              ))}
            </div>
          </div>

          <form className="search-section" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search routines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit">Search</button>
          </form>
        </div>

        {/* Results Count */}
        <div className="results-info">
          <span>
            {totalCount} routine{totalCount !== 1 ? "s" : ""} found
          </span>
        </div>

        {/* Routines Grid */}
        {loading ? (
          <RoutinesGridSkeleton count={6} />
        ) : routines.length === 0 ? (
          <div className="no-routines">
            <div className="no-routines-icon">🧴</div>
            <h3>No routines found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="routines-grid">
            {routines.map((routine) => (
              <div
                key={routine.routine_id}
                className="routine-card"
                onClick={() => handleRoutineClick(routine)}
              >
                <div
                  className="routine-image"
                  style={{
                    backgroundImage: `url(${getImageUrl(routine.cover_image)})`,
                  }}
                >
                  <div className="routine-overlay">
                    <span className="view-routine">View Routine →</span>
                  </div>
                </div>
                <div className="routine-content">
                  <div className="routine-skin-types">
                    {routine.skin_types?.slice(0, 3).map((st) => (
                      <span key={st.skin_type_id} className="skin-tag">
                        {st.name}
                      </span>
                    ))}
                    {routine.skin_types?.length > 3 && (
                      <span className="skin-tag more">
                        +{routine.skin_types.length - 3}
                      </span>
                    )}
                  </div>
                  <h3 className="routine-title">{routine.title}</h3>
                  <p
                    className="routine-description"
                    dangerouslySetInnerHTML={{
                      __html:
                        routine.description?.replace(/<[^>]*>/g, "").slice(0, 100) +
                        (routine.description?.length > 100 ? "..." : ""),
                    }}
                  ></p>
                  <div className="routine-meta">
                    <div className="meta-item">
                      <span className="meta-label">Products</span>
                      <span className="meta-value">{routine.product_count || 0}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Total Price</span>
                      <span className="meta-value price">
                        {routine.coupon && routine.discounted_total < routine.total_price ? (
                          <>
                            <span className="original-price">{formatMoney(routine.total_price || 0)}</span>
                            {formatMoney(routine.discounted_total || 0)}
                          </>
                        ) : (
                          formatMoney(routine.total_price || 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Routines;

